import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Badge,
  BADGE_COLORS,
  BADGE_MODES,
  type BadgeColor,
  type BadgeMode,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './BadgeShowcase.css'

type ShowcaseVariant = {
  mode: BadgeMode
  dismissible: boolean
  label: string
}

const SHOWCASE_VARIANTS: ShowcaseVariant[] = [
  { mode: 'solid', dismissible: false, label: 'Solid' },
  { mode: 'tinted', dismissible: false, label: 'Tinted' },
  { mode: 'solid', dismissible: true, label: 'Solid + dismiss' },
  { mode: 'tinted', dismissible: true, label: 'Tinted + dismiss' },
]

const formatColorLabel = (color: BadgeColor): string =>
  color
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const BadgeShowcase: React.FC = () => {
  const [color, setColor] = useState<BadgeColor>('gray')
  const [mode, setMode] = useState<BadgeMode>('solid')
  const [dismissible, setDismissible] = useState(false)
  const [label, setLabel] = useState('Label')
  const rowRefs = useRef<Partial<Record<BadgeColor, HTMLDivElement | null>>>({})

  useEffect(() => {
    rowRefs.current[color]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [color])

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'badge-color',
            label: 'Highlighted color',
            control: (
              <select
                id="badge-color"
                className="ui-field-control"
                value={color}
                onChange={(event) => setColor(event.target.value as BadgeColor)}
              >
                {BADGE_COLORS.map((option) => (
                  <option key={option} value={option}>
                    {formatColorLabel(option)}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'badge-mode',
            label: 'Mode',
            control: (
              <select
                id="badge-mode"
                className="ui-field-control"
                value={mode}
                onChange={(event) => setMode(event.target.value as BadgeMode)}
              >
                {BADGE_MODES.map((option) => (
                  <option key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'badge-dismissible',
            label: 'Dismissible',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="badge-dismissible"
                  type="checkbox"
                  checked={dismissible}
                  onChange={(event) => setDismissible(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'badge-label',
            label: 'Label',
            control: (
              <input
                id="badge-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
        ],
      },
    ],
    [color, dismissible, label, mode],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Badge"
        description="Solid and tinted color variants with optional dismiss. Dark mode uses the solid (saturated) appearance."
        stageClassName="ui-preview__stage--wide badge-showcase__stage-wrap"
      >
        <div className={`badge-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Badge with the selected color, mode, and label from the panel.
            </p>
            <Badge
              mode={mode}
              color={color}
              label={label}
              dismissible={dismissible}
            />
          </section>

          <div className="badge-showcase__sheet-header">
            <span className="badge-showcase__sheet-header-label">Color</span>
            {SHOWCASE_VARIANTS.map((variant) => (
              <span
                key={`${variant.mode}-${variant.dismissible}`}
                className="badge-showcase__sheet-header-label badge-showcase__sheet-header-label--variant"
              >
                {variant.label}
              </span>
            ))}
          </div>

          {BADGE_COLORS.map((colorOption) => (
            <div
              key={colorOption}
              ref={(element) => {
                rowRefs.current[colorOption] = element
              }}
              className={`badge-showcase__row${
                colorOption === color ? ' badge-showcase__row--selected' : ''
              }`}
            >
              <span className="badge-showcase__row-label">
                {formatColorLabel(colorOption)}
              </span>
              {SHOWCASE_VARIANTS.map((variant) => (
                <div
                  key={`${colorOption}-${variant.mode}-${variant.dismissible}`}
                  className="badge-showcase__cell"
                >
                  <Badge
                    mode={variant.mode}
                    color={colorOption}
                    label={label}
                    dismissible={variant.dismissible}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Badge" sections={propertySections} />
    </>
  )
}

export default BadgeShowcase
